import React, { useState, useEffect, useCallback } from 'react';
import './ProductRequests.css';
import { getGroupedRequests, updateGroupStatus } from './requestsHelpers';

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  pending:     'Pending',
  researching: 'Researching',
  approved:    'Approved',
  added:       'Added',
  rejected:    'Rejected',
};

const SOURCE_LABELS = {
  search_miss:  'search miss',
  product_page: 'store request',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

// ── RequestGroup ──────────────────────────────────────────────────────────────
// Single grouped row in the demand dashboard.

function RequestGroup({ group, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);

  const isDone        = ['added', 'rejected'].includes(group.status);
  const searchMisses  = group.sources.search_miss  || 0;
  const pageRequests  = group.sources.product_page || 0;
  const storesInGroup = [...new Set(group.items.filter(i => i.store).map(i => i.store))];

  return (
    <div className={`req-group req-group--${group.status}`}>

      {/* ── Collapsed header ── */}
      <button
        className="req-group-header"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
      >
        <div className="req-group-header-left">
          <span className="req-group-name">
            {group.productName}
            {storesInGroup.length === 1 && pageRequests > 0 && (
              <span className="req-group-store-hint"> · {storesInGroup[0]} missing</span>
            )}
          </span>
          <div className="req-group-sources">
            {searchMisses > 0 && (
              <span className="req-source-chip req-source-chip--miss">
                {searchMisses} {searchMisses === 1 ? 'search miss' : 'search misses'}
              </span>
            )}
            {pageRequests > 0 && (
              <span className="req-source-chip req-source-chip--page">
                {pageRequests} {pageRequests === 1 ? 'store request' : 'store requests'}
              </span>
            )}
          </div>
        </div>

        <div className="req-group-header-right">
          <span className="req-signal-count">{group.signalCount}</span>
          <span className={`req-status-badge req-status-badge--${group.status}`}>
            {STATUS_LABELS[group.status]}
          </span>
          <span className="req-chevron" aria-hidden="true">{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {/* ── Expanded body ── */}
      {expanded && (
        <div className="req-group-body">

          {/* Date range */}
          <div className="req-group-dates">
            <span>First seen: {formatDate(group.firstSeenAt)}</span>
            <span>Last seen: {formatDate(group.lastSeenAt)}</span>
          </div>

          {/* Individual signal log */}
          <div className="req-items-list">
            {group.items.map(item => (
              <div key={item.requestId} className="req-item-row">
                <span className="req-item-date">{formatDate(item.timestamp)}</span>
                <span className={`req-item-source req-item-source--${item.source}`}>
                  {SOURCE_LABELS[item.source] || item.source}
                </span>
                {item.store && <span className="req-item-store">{item.store}</span>}
                {item.brand && <span className="req-item-brand">{item.brand}</span>}
                {item.size  && <span className="req-item-size">{item.size}</span>}
              </div>
            ))}
          </div>

          {/* Status actions */}
          <div className="req-actions">
            {!isDone ? (
              <>
                {group.status !== 'researching' && (
                  <button
                    className="req-action-btn req-action-btn--research"
                    onClick={() => onStatusChange(group.normalizedProductName, 'researching')}
                  >
                    Researching
                  </button>
                )}
                {group.status !== 'approved' && (
                  <button
                    className="req-action-btn req-action-btn--approve"
                    onClick={() => onStatusChange(group.normalizedProductName, 'approved')}
                  >
                    ✓ Approved
                  </button>
                )}
                <button
                  className="req-action-btn req-action-btn--add"
                  onClick={() => onStatusChange(group.normalizedProductName, 'added')}
                >
                  ✓ Added to library
                </button>
                <button
                  className="req-action-btn req-action-btn--reject"
                  onClick={() => onStatusChange(group.normalizedProductName, 'rejected')}
                >
                  ✗ Reject
                </button>
              </>
            ) : (
              <button
                className="req-action-btn req-action-btn--reopen"
                onClick={() => onStatusChange(group.normalizedProductName, 'pending')}
              >
                ↩ Reopen
              </button>
            )}
          </div>

        </div>
      )}
    </div>
  );
}

// ── ProductRequests ───────────────────────────────────────────────────────────

export default function ProductRequests() {
  const [groups, setGroups] = useState([]);
  const [filter, setFilter] = useState('active'); // 'active' | 'all' | 'done'

  const reload = useCallback(() => setGroups(getGroupedRequests()), []);
  useEffect(() => { reload(); }, [reload]);

  const handleStatusChange = (normalizedProductName, status) => {
    updateGroupStatus(normalizedProductName, status);
    reload();
  };

  const filtered = groups.filter(g => {
    if (filter === 'active') return !['added', 'rejected'].includes(g.status);
    if (filter === 'done')   return ['added', 'rejected'].includes(g.status);
    return true;
  });

  const activeCount  = groups.filter(g => !['added', 'rejected'].includes(g.status)).length;
  const totalSignals = filtered.reduce((sum, g) => sum + g.signalCount, 0);

  return (
    <div className="req-panel">

      {/* ── Summary ── */}
      <div className="req-summary">
        <div className="req-summary-pill">
          <span className="req-summary-count">{activeCount}</span>
          <span className="req-summary-label">Active products</span>
        </div>
        <div className="req-summary-pill">
          <span className="req-summary-count">{totalSignals}</span>
          <span className="req-summary-label">Signals shown</span>
        </div>
      </div>

      {/* ── Filter tabs ── */}
      <div className="req-filter-tabs">
        {[
          { key: 'active', label: 'Active' },
          { key: 'all',    label: 'All'    },
          { key: 'done',   label: 'Done'   },
        ].map(f => (
          <button
            key={f.key}
            className={`req-filter-tab${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Groups list ── */}
      {filtered.length === 0 ? (
        <div className="req-empty">
          <p className="req-empty-title">
            {filter === 'active' ? 'No active requests yet'
           : filter === 'done'   ? 'Nothing marked as done'
           : 'No requests yet'}
          </p>
          <p className="req-empty-sub">
            {filter === 'active'
              ? "Search misses appear here automatically when users can't find a product. Store version requests appear when users tap a missing store row on a product page."
              : ''}
          </p>
        </div>
      ) : (
        <div className="req-groups-list">
          {filtered.map(g => (
            <RequestGroup
              key={g.normalizedProductName}
              group={g}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}

    </div>
  );
}
